import { EarlyAccessDialog } from "@/components/EarlyAccessDialog";
import { Layout } from "@/components/Layout";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Check, X, Sparkles, Rocket, TrendingUp, Crown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

type Tier = "spark" | "accelerate" | "amplify" | "dominate";

interface TierInfo {
  id: Tier;
  name: string;
  price: string;
  priceDetail: string;
  priceAnnual?: string;
  priceAnnualDetail?: string;
  monthlyAmount?: number;
  annualMonthlyAmount?: number;
  tagline: string;
  features: string[];
  notIncluded?: string[];
}

const TIERS: TierInfo[] = [
  {
    id: "spark",
    name: "SPARK",
    price: "Free",
    priceDetail: "Forever",
    tagline: "Source verified deadstock and sorted secondhand",
    features: [
      "Browse every verified lot",
      "Buy deadstock and sorted secondhand",
      "Bid on remake projects — free for now",
      "Standard buyer support",
    ],
    notIncluded: ["No listing", "No posting remake projects"],
  },
  {
    id: "accelerate",
    name: "ACCELERATE",
    price: "€99",
    priceDetail: "/month",
    priceAnnual: "€79",
    priceAnnualDetail: "/mo billed yearly",
    monthlyAmount: 99,
    annualMonthlyAmount: 79,
    tagline: "List surplus, bid on remake projects",
    features: [
      "Everything in SPARK",
      "Up to 10 active lots",
      "Up to 10 sold lots / month",
      "15% platform fee",
    ],
    notIncluded: ["No posting remake projects"],
  },
  {
    id: "amplify",
    name: "SCALE",
    price: "€149",
    priceDetail: "/month",
    priceAnnual: "€119",
    priceAnnualDetail: "/mo billed yearly",
    monthlyAmount: 149,
    annualMonthlyAmount: 119,
    tagline: "Scale your listings, lower fees",
    features: [
      "Everything in ACCELERATE",
      "Up to 25 active lots",
      "Up to 25 sold lots / month",
      "12.5% platform fee",
    ],
    notIncluded: ["No posting remake projects"],
  },
  {
    id: "dominate",
    name: "DOMINATE",
    price: "€299",
    priceDetail: "/month",
    priceAnnual: "€239",
    priceAnnualDetail: "/mo billed yearly",
    monthlyAmount: 299,
    annualMonthlyAmount: 239,
    tagline: "Unlimited scale, post your own remake projects",
    features: [
      "Everything in ACCELERATE",
      "Unlimited lots and sales",
      "10% platform fee",
      "Post unlimited remake projects",
      "Bids from verified remakers",
      "Priority support",
    ],
  },
];

const TIER_ICON: Record<Tier, React.ComponentType<{ className?: string }>> = {
  spark: Sparkles,
  accelerate: Rocket,
  amplify: TrendingUp,
  dominate: Crown,
};

const POPULAR_TIER: Tier = "accelerate";

const Pricing = () => {
  const [billing, setBilling] = useState<"monthly" | "annual">("annual");

  return (
    <Layout>
      <Helmet>
        <title>Pricing | HIDNstock B2B Marketplace</title>
        <meta
          name="description"
          content="HIDNstock pricing: start free to source deadstock and sorted secondhand in bulk, or upgrade to list surplus, lower platform fees and post remake projects."
        />
        <link rel="canonical" href="https://hidnstock.com/pricing" />
        <meta property="og:title" content="Pricing | HIDNstock B2B Marketplace" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hidnstock.com/pricing" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <section className="container mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-3xl mb-12">
          <p className="text-xs font-black uppercase text-muted-foreground mb-4">
            01 — Pricing
          </p>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
            Pick your plan.
            <br />
            Scale your surplus.
          </h1>
          <p className="mt-6 text-base font-bold uppercase text-muted-foreground">
            Buying is free. Pay only when you want to sell, scale and post your own remake projects.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex border-2 border-foreground bg-card p-1">
            {(["monthly", "annual"] as const).map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setBilling(b)}
                className={cn(
                  "px-5 py-2 text-xs font-black uppercase tracking-widest transition-colors",
                  billing === b
                    ? "bg-foreground text-background"
                    : "text-foreground hover:bg-foreground/10",
                )}
              >
                {b === "monthly" ? "Monthly" : "Annual"}
                {b === "annual" && (
                  <span
                    className={cn(
                      "ml-2 px-1.5 py-0.5 text-[9px] tracking-wider",
                      billing === "annual"
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary/20 text-primary",
                    )}
                  >
                    Save 20%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-6 lg:gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-stretch">
          {TIERS.map((t) => {
            const Icon = TIER_ICON[t.id];
            const isPopular = t.id === POPULAR_TIER;
            const showAnnual = billing === "annual" && t.priceAnnual;
            const displayPrice = showAnnual ? t.priceAnnual! : t.price;
            const displayDetail = showAnnual ? t.priceAnnualDetail! : t.priceDetail;
            const savings =
              billing === "annual" && t.monthlyAmount && t.annualMonthlyAmount
                ? (t.monthlyAmount - t.annualMonthlyAmount) * 12
                : 0;

            return (
              <div key={t.id} className="relative flex">
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="text-[10px] font-bold uppercase tracking-widest px-3 py-1 bg-foreground text-background">
                      Most popular
                    </span>
                  </div>
                )}
                <div
                  className={cn(
                    "w-full p-6 lg:p-7 border-2 flex flex-col bg-card transition-all",
                    "hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_hsl(var(--foreground))]",
                    isPopular ? "border-foreground" : "border-border hover:border-foreground",
                  )}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Icon className="h-5 w-5 text-primary shrink-0" />
                    <span className="font-black uppercase tracking-tighter text-lg whitespace-nowrap">
                      {t.name}
                    </span>
                  </div>

                  <div className="min-h-[56px] mb-6 flex items-start">
                    <p className="text-sm font-bold uppercase leading-snug text-muted-foreground">
                      {t.tagline}
                    </p>
                  </div>

                  <div className="mb-8 min-h-[76px]">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-4xl lg:text-5xl font-black tracking-tighter">
                        {displayPrice}
                      </span>
                      <span className="text-xs font-bold uppercase text-muted-foreground">
                        {displayDetail}
                      </span>
                    </div>
                    {savings > 0 && (
                      <p className="mt-1.5 text-[10px] font-black uppercase tracking-widest text-primary">
                        Save €{savings}/yr
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3 flex-grow border-t-2 border-border pt-6 mb-8">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span className="font-bold text-sm uppercase leading-snug">{f}</span>
                      </li>
                    ))}
                    {t.notIncluded?.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-muted-foreground/70">
                        <X className="h-4 w-4 shrink-0 mt-0.5" />
                        <span className="font-bold text-sm uppercase leading-snug line-through decoration-muted-foreground/40">
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <EarlyAccessDialog><Button className="mt-auto w-full uppercase font-black tracking-normal">Get started</Button></EarlyAccessDialog>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 border-2 border-foreground p-8 lg:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter">
              Ready to move your excess?
            </h2>
            <p className="mt-2 text-sm font-bold uppercase text-muted-foreground">
              Create your account and start buying or listing today.
            </p>
          </div>
          <EarlyAccessDialog><Button size="lg" className="uppercase font-black tracking-normal">Get started</Button></EarlyAccessDialog>
        </div>
      </section>
    </Layout>
  );
};

export default Pricing;
